import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, take, takeUntil, tap } from 'rxjs/operators';
import { UserService } from '../../../core/services/user.service';
import { RoleService } from '../../../core/services/role.service';
import { ClientService } from '../../../core/services/client.service';
import { Application, Role, RoleDetailsDTO } from '../../../core/models/role.model';
import { AuthorizationEvent } from '../../../core/models/habilitation-history.model';
import { UserRolesDialogComponent } from '../../roles/user-roles-dialog/user-roles-dialog.component';
import { UserGroupsDialogComponent } from '../user-groups-dialog/user-groups-dialog.component';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../core/services/notification.service';
import { User } from '../../../core/models/user.model';
import { UserHistoryService } from '../../../core/services/user-history.service';
import { UserHistoryEvent } from '../../../core/models/user-history.model';
import { GroupService } from '../../../core/services/group.service';

// Interface pour typer les données stockées
interface StoredAppsRoles {
  resource_access?: Record<string, { roles: string[] }>;
  timestamp?: string;
  _filtered?: boolean;
}

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    FormsModule
  ],
  templateUrl: './user-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-detail.component.scss']
})
export class UserDetailComponent implements OnInit, OnDestroy {
  user: User | null = null;
  userRoles: Role[] = [];
  filteredEvents: UserHistoryEvent[] = [];
  userGroups: { id: string; name: string; path: string }[] = [];

  applications: Application[] = [];
  isLoading = false;
  loading = false;
  isLoadingRoles = false;
  isLoadingEvents = false;
  isLoadingGroups = false;
  rolesError: string | null = null;

  rolessauvegardes: StoredAppsRoles | null = null;

  private activeClientId: any;
  private destroy$ = new Subject<void>();
  activeDropdownId: string | null = null;
  activeTab: number = 1;
  searchTerm: string = '';

  // Propriété pour stocker la clé de l'application actuellement dépliée (null = aucune)
  expandedApp: string | null = null;

  userAppsWithRoles: { [appName: string]: { [roleName: string]: RoleDetailsDTO } } | null = null;
  loadingAppsWithRoles = false;

  private notificationService = inject(NotificationService);
  public processingActions = new Set<string>(); // 'reset' | 'verify'

  /** Libellés affichés (résolus depuis les codes Keycloak) */
  iesDisplayLabel = '';
  etablissementsDisplayLabels: string[] = [];

  isResolvingIes = false;
  isResolvingEtab = false;

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private roleService: RoleService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private clientService: ClientService,
    private userHistoryService: UserHistoryService,
    private groupService: GroupService
  ) { }

  ngOnInit(): void {
    const userId = this.route.snapshot.paramMap.get('id');
    this.activeTab = 2;

    if (!userId) {
      this.showErrorMessage('ID utilisateur manquant');
      return;
    }

    this.loading = true;
    this.isLoadingEvents = true;
    this.isLoadingGroups = true;

    // Charger les rôles sauvegardés depuis le localStorage
    this.loadStoredRoles();

    // Vérifier le localStorage pour debug
    this.checkStoredEvents(userId);

    this.clientService.getActiveClient().subscribe(() => {
      this.activeClientId = "ac941bf2-efd2-4f99-a47d-44c365345a5c";

      if (!userId) {
        this.showErrorMessage('Aucun client sélectionné');
        this.loading = false;
        return;
      }

      // console.log('Chargement des données utilisateur...', {
      //   userId,
      //   hasStoredRoles: !!this.rolessauvegardes
      // });

      // Charger l'utilisateur et les rôles en parallèle
      forkJoin({
        user: this.loadUserAsync(userId),
        roles: this.loadUserRolesAsync(userId, this.activeClientId),
        groups: this.loadUserGroupsAsync(userId)
      }).subscribe({
        next: (results) => {
          this.resolveUserRattachement();

          // Charger les événements séparément
          this.loadEventsAsync(userId).subscribe({
            next: (events) => {
              this.filteredEvents = events;
              this.loading = false;
              this.isLoadingEvents = false;
            },
            error: (error) => {
              console.error(' Erreur chargement événements:', error);
              this.filteredEvents = [];
              this.loading = false;
              this.isLoadingEvents = false;
            }
          });

          // Charger les applications avec rôles
          this.loadApplicationsWithRoles(userId);
        },
        error: (error) => {
          this.loading = false;
          this.isLoadingEvents = false;
          this.showErrorMessage('Erreur lors du chargement des données');
        }
      });
    });
  }

  // Cumule le nombre total de rôles dans toutes les applications
  // get totalRolesCount(): number {
  //   if (!this.rolessauvegardes?.resource_access) {
  //     return 0;
  //   }

  //   return Object.values(this.rolessauvegardes.resource_access)
  //     .reduce((total, appData) => {
  //       return total + (appData.roles?.length || 0);
  //     }, 0);
  // }

  get totalRolesCount(): number {
    if (!this.userAppsWithRoles) return 0;
    let count = 0;
    for (const app in this.userAppsWithRoles) {
      count += Object.keys(this.userAppsWithRoles[app]).length;
    }
    return count;
  }

  /**
   * Charge les rôles sauvegardés depuis le localStorage
   */
  private loadStoredRoles(): void {
    try {
      const stored = localStorage.getItem('appsRolesUser');

      if (stored) {
        const parsed = JSON.parse(stored);

        // Filtrage des applications système
        if (parsed.resource_access) {
          const SYSTEM_APPS = ['realm-management', 'broker', 'account'];
          const filtered: Record<string, any> = {};

          Object.keys(parsed.resource_access).forEach(key => {
            if (!SYSTEM_APPS.includes(key)) {
              filtered[key] = parsed.resource_access[key];
            }
          });

          parsed.resource_access = filtered;
          parsed._filtered = true;
        }

        this.rolessauvegardes = parsed;
      } else {
        this.rolessauvegardes = null;
      }
    } catch (error) {
      console.error('Erreur lors du chargement des rôles sauvegardés:', error);
      this.rolessauvegardes = null;
    }
  }

  /**
   * Vérifie le contenu du localStorage pour debug
   */
  private checkStoredEvents(userId: string): void {
    const storedEvents = localStorage.getItem('processedEvents');
    // console.log('Vérification localStorage "processedEvents":');

    if (storedEvents) {
      try {
        const parsed = JSON.parse(storedEvents);

        // Rechercher des événements pour cet utilisateur
        if (Array.isArray(parsed)) {
          const userEvents = parsed.filter((event: any) => {
            return event.userId === userId ||
              (event.user && event.user.id === userId) ||
              (event.authDetails && event.authDetails.user && event.authDetails.user.id === userId);
          });

          if (userEvents.length > 0) {
            // console.log('Détails du premier événement:', userEvents[0]);
          }
        }
      } catch (error) {
        // console.error('Erreur parsing localStorage:', error);
      }
    } else {
      // console.log('Aucune donnée dans "processedEvents"');
    }
  }

  /**
   * Méthode utilitaire pour vérifier si l'utilisateur a un rôle spécifique
   */
  hasRoleInApp(appName: string, roleName: string): boolean {
    if (!this.rolessauvegardes?.resource_access?.[appName]) {
      return false;
    }

    return this.rolessauvegardes.resource_access[appName].roles.includes(roleName);
  }

  /**
   * Récupère toutes les applications où l'utilisateur a des rôles
   */
  // getUserApps(): string[] {
  //   if (!this.rolessauvegardes?.resource_access) {
  //     return [];
  //   }

  //   return Object.keys(this.rolessauvegardes.resource_access);
  // }

  getUserApps(): string[] {
    return this.userAppsWithRoles ? Object.keys(this.userAppsWithRoles) : [];
  }

  private loadApplicationsWithRoles(userId: string): void {
    this.loadingAppsWithRoles = true;
    this.roleService.getUserApplicationsWithRoles(userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.userAppsWithRoles = data;
          this.loadingAppsWithRoles = false;
        },
        error: (error) => {
          console.error('Erreur chargement applications avec rôles:', error);
          this.userAppsWithRoles = null;
          this.loadingAppsWithRoles = false;
        }
      });
  }

  private loadRolesForAllApplications(): any {
    if (this.applications.length === 0) {
      this.isLoading = false;
      return;
    }

    const roleRequests = this.applications.map(app =>
      this.roleService.getClientRoles(app.id)
    );

    forkJoin(roleRequests).subscribe({
      next: (responses) => {
        // console.log('🔑 RÔLES APPLICATIONS CHARGÉS:', responses);

        // Initialiser les rôles pour chaque application
        responses.forEach((response, index) => {
          this.applications[index].roles = response.content || response || [];
          // console.log(`Application ${this.applications[index].name} - Rôles:`, this.applications[index].roles);
        });
      },
      error: (error) => {
        console.error('Erreur lors du chargement des rôles:', error);
        this.showErrorMessage('Erreur lors du chargement des rôles');
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Méthode pour changer de tab
  private processedEvents: AuthorizationEvent[] | undefined;
  switchTab(tabNumber: number): void {
    this.activeTab = tabNumber;
    window.scrollTo(0, 0);
    // console.log(`Navigation vers le tab ${tabNumber}`);
  }

  private loadUserAsync(userId: string): Observable<User | null> {
    return this.userService.getUserById(userId).pipe(
      tap(user => {
        this.user = user;
      }),
      catchError(error => {
        console.error('Erreur chargement utilisateur:', error);
        this.showErrorMessage('Erreur lors du chargement de l\'utilisateur');
        return of(null);
      })
    );
  }

  private loadUserRolesAsync(userId: string, clientId: string): Observable<any> {
    this.isLoadingRoles = true;

    return this.roleService.getUserRoles(userId, clientId).pipe(
      tap(response => {
        this.userRoles = response.content || [];
        this.isLoadingRoles = false;
      }),
      catchError(error => {
        console.error('❌ Erreur chargement rôles:', error);
        this.rolesError = error.message;
        this.isLoadingRoles = false;
        this.showErrorMessage('Erreur lors du chargement des rôles');
        return of([]);
      })
    );
  }

  private loadUserGroupsAsync(userId: string): Observable<any> {
    this.isLoadingGroups = true;
    return this.userService.getUserGroupsForUser(userId).pipe(
      tap(groups => {
        this.userGroups = groups || [];
        this.isLoadingGroups = false;
      }),
      catchError(error => {
        console.error('Erreur chargement groupes:', error);
        this.isLoadingGroups = false;
        this.userGroups = [];
        return of([]);
      })
    );
  }

  private loadEventsAsync(userId: string): Observable<UserHistoryEvent[]> {
    this.isLoadingEvents = true;

    return new Observable(observer => {
      this.loadFilteredEventsWithFallback(userId)
        .then((events) => {
          this.isLoadingEvents = false;
          observer.next(events);
          observer.complete();
        })
        .catch(error => {
          console.error('Erreur chargement événements:', error);
          this.isLoadingEvents = false;
          observer.next([]);
          observer.complete();
        });
    });
  }

  private async loadFilteredEventsWithFallback(userId: string): Promise<UserHistoryEvent[]> {
    try {
      // Essayer le localStorage d'abord
      const storedEvents = localStorage.getItem('processedEvents');

      if (storedEvents) {
        const processedEvents = JSON.parse(storedEvents);

        // Vérifier le format des données
        if (!Array.isArray(processedEvents)) {
          console.error('processedEvents n\'est pas un tableau:', processedEvents);
          return await this.loadEventsFromAPI(userId);
        }

        const filtered = processedEvents.filter((event: UserHistoryEvent) =>
          event.targetUser?.id === userId
        );

        if (filtered.length > 0) {
          return filtered;
        } else {
          // console.log('Aucun événement trouvé dans localStorage pour cet utilisateur');
        }
      } else {
        // console.log('ℹAucune donnée dans localStorage');
      }

      // Si localStorage vide ou pas d'événements, charger depuis API
      return await this.loadEventsFromAPI(userId);
    } catch (error) {
      console.error('Erreur chargement événements:', error);
      return await this.loadEventsFromAPI(userId);
    }
  }

  private loadEventsFromAPI(userId: string): Promise<UserHistoryEvent[]> {
    return new Promise((resolve) => {
      this.userHistoryService.getUserHistory({ first: 0, max: 100 })
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            const events = (response.content ?? [])
              .filter(e => e.targetUser?.id === userId || e.resourcePath?.includes(userId))
              .sort((a, b) => b.timestamp - a.timestamp);
            resolve(events as any);
          },
          error: (err) => {
            console.error('Erreur API historique utilisateur:', err);
            resolve([]);
          }
        });
    });
  }

  // // Méthode pour traduire les types d'opération
  // getActionLabel(operationType: string): string {
  //   const labels: { [key: string]: string } = {
  //     'CREATE': 'Affectation',
  //     'UPDATE': 'Modification',
  //     'DELETE': 'Retrait',
  //     'ADD': 'Ajout',
  //     'REMOVE': 'Suppression'
  //   };
  //   return labels[operationType] || operationType;
  // }

  openRolesDialog(user: User): void {
    const dialogRef = this.dialog.open(UserRolesDialogComponent, {
      width: '750px',
      data: {
        panelClass: 'custom-roles-dialog',
        user: user,
        allRoles: []
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      if (this.user?.id && this.activeClientId) {
        this.loadUserRolesAsync(this.user.id, this.activeClientId).subscribe();
      }
    });
  }

  openGroupsDialog(): void {
    if (!this.user) {
      this.showErrorMessage('Utilisateur non chargé');
      return;
    }

    const dialogRef = this.dialog.open(UserGroupsDialogComponent, {
      width: '950px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { user: this.user },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.user?.id && this.activeClientId) {
        this.loadUserAsync(this.user.id).subscribe();
      }
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  sendEmailVerification(user: User): void {
    if (!user.id || this.processingActions.has('verify') || user.emailVerified) return;
    this.processingActions.add('verify');

    this.userService.sendVerificationEmail(user.id).subscribe({
      next: () => {
        this.processingActions.delete('verify');
        this.notificationService.showSuccess({
          title: 'Email envoyé',
          message: 'Un email de vérification a été envoyé.'
        });
      },
      error: (error) => {
        this.processingActions.delete('verify');
        const msg = error.status === 403
          ? 'Vous n\'avez pas l\'autorisation.'
          : error.error?.error || 'Échec de l\'envoi.';
        this.notificationService.showError({ title: 'Erreur', message: msg });
      }
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  onResetPassword(user: User): void {
    if (!user.id || this.processingActions.has('reset')) return;
    this.processingActions.add('reset');

    this.userService.sendResetPasswordEmail(user.id).subscribe({
      next: () => {
        this.processingActions.delete('reset');
        this.notificationService.showSuccess({
          title: 'Email envoyé',
          message: 'Un email de réinitialisation a été envoyé.'
        });
      },
      error: (error) => {
        this.processingActions.delete('reset');
        const msg = error.status === 403
          ? 'Vous n\'avez pas l\'autorisation.'
          : error.error?.error || 'Erreur lors de l\'envoi.';
        this.notificationService.showError({ title: 'Erreur', message: msg });
      }
    });
  }

  resetRoles() {
    // console.log('Réinitialisation des rôles');
  }

  saveHistory() {
    // console.log('Sauvegarde de l\'historique');
  }

  filterHistory() {
    if (!this.searchTerm.trim()) {
      const userId = this.route.snapshot.paramMap.get('id');
      if (userId) {
        this.loadEventsAsync(userId).subscribe(events => {
          this.filteredEvents = events;
        });
      }
      return;
    }

    const term = this.searchTerm.toLowerCase().trim();
    this.filteredEvents = this.filteredEvents.filter(event =>
      this.getActionLabel(event.operationType).toLowerCase().includes(term) ||
      this.getActorName(event).toLowerCase().includes(term) ||
      (event.targetUser?.username?.toLowerCase().includes(term) ?? false) ||
      (event.resourcePath?.toLowerCase().includes(term) ?? false) ||
      new Date(event.timestamp).toLocaleDateString('fr-FR').toLowerCase().includes(term)
    );
  }

  // Méthode pour basculer l'état d'expansion d'une application
  toggleApp(appKey: string): void {
    this.expandedApp = this.expandedApp === appKey ? null : appKey;
  }

  trackByAppKey(index: number, app: any): string {
    return app.key;
  }

  protected readonly Object = Object;

  /** Date de création formatée */
  getCreationDate(): string {
    if (!this.user?.createdTimestamp) return 'Date inconnue';
    return new Date(this.user.createdTimestamp).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }

  /** Établissements sous forme de chaîne lisible */
  getEtablissementsLabel(): string {
    if (this.etablissementsDisplayLabels.length > 0) {
      return this.etablissementsDisplayLabels.join(', ');
    }
    const list = this.user?.sigleEtablissements;
    if (!list || list.length === 0) return '—';
    return list.join(', ');
  }

  /** Vrai si le user a un rattachement IES ou établissement */
  hasRattachement(): boolean {
    return !!(this.user?.sigleIes ||
      (this.user?.sigleEtablissements && this.user.sigleEtablissements.length > 0));
  }

  /** Libellé humain du type d'opération */
  getActionLabel(operationType: string): string {
    const labels: { [key: string]: string } = {
      // Habilitations
      'CREATE': 'Attribution de rôle',
      'UPDATE': 'Modification',
      'DELETE': 'Retrait de rôle',
      'ADD': 'Ajout',
      'REMOVE': 'Suppression',
      // Événements utilisateurs
      'USER_CREATE': 'Création utilisateur',
      'USER_UPDATE': 'Modification',
      'USER_DELETE': 'Suppression',
      'ROLE_GRANTED': 'Attribution de rôle',
      'ROLE_REVOKED': 'Retrait de rôle',
      'RESET_PASSWORD': 'Réinitialisation mot de passe',
      'UPDATE_PASSWORD': 'Mot de passe modifié',
      'SEND_RESET_PASSWORD': 'Email de réinitialisation',
      'SEND_VERIFY_EMAIL': 'Email de vérification',
      'VERIFY_EMAIL': 'Email vérifié',
      'UPDATE_EMAIL': 'Email modifié',
      'DISABLE_USER': 'Révocation',
      'ENABLE_USER': 'Réactivation',
      'USER_DISABLED': 'Révocation',
      'USER_ENABLED': 'Réactivation',
      'UPDATE_PROFILE': 'Modification profil'
    };
    return labels[operationType] || operationType;
  }

  /** Classe CSS du badge selon le type d'action */
  getActionBadgeClass(operationType: string): string {
    const success = ['CREATE', 'USER_CREATE', 'ROLE_GRANTED', 'ENABLE_USER',
      'USER_ENABLED', 'VERIFY_EMAIL', 'UPDATE_PASSWORD'];
    const warning = ['UPDATE', 'USER_UPDATE', 'UPDATE_PROFILE', 'SEND_RESET_PASSWORD',
      'SEND_VERIFY_EMAIL', 'RESET_PASSWORD', 'UPDATE_EMAIL'];
    const error = ['DELETE', 'REMOVE', 'USER_DELETE', 'ROLE_REVOKED',
      'DISABLE_USER', 'USER_DISABLED'];

    if (success.includes(operationType)) return 'success';
    if (warning.includes(operationType)) return 'warning';
    if (error.includes(operationType)) return 'error';
    return 'neutral';
  }

  /** Nom de l'admin auteur de l'action */
  getActorName(event: UserHistoryEvent): string {
    const a = event.admin;
    if (!a) return 'Système';
    const full = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
    return full || a.username || 'Utilisateur inconnu';
  }

  /** Email de l'admin (pour la sous-ligne) */
  getActorEmail(event: UserHistoryEvent): string | null {
    return event.admin?.email ?? null;
  }

  /** Libellé de la cible pour la colonne Détail */
  getEventDetail(event: UserHistoryEvent): string {
    // 1. Le path brut peut indiquer la sous-ressource (ex: users/<id>/role-mappings)
    const path = event.resourcePath ?? '';
    const segments = path.split('/');
    const extra = segments.slice(2).join('/');   // ignore "users/<uuid>"

    // 2. On privilégie le username de la cible s'il est résolu
    const targetName =
      event.targetUser?.username ||
      (event.targetUser?.firstName && event.targetUser?.lastName
        ? `${event.targetUser.firstName} ${event.targetUser.lastName}`
        : null);

    if (extra) return extra;           // ex: "role-mappings/clients/…"
    return targetName ?? '—';
  }

  private resolveUserRattachement(): void {
    this.iesDisplayLabel = '';
    this.etablissementsDisplayLabels = [];
    this.isResolvingIes = false;
    this.isResolvingEtab = false;

    if (!this.user) return;

    const iesCode = this.user.sigleIes;
    const etabCodes = this.user.sigleEtablissements || [];

    // Cas 1 : pas d'IES — on résout quand même les étabs (code → sigle)
    if (!iesCode) {
      if (etabCodes.length === 0) return;

      this.isResolvingEtab = true;
      const requests = etabCodes.map(code =>
        this.userService.getSigleByCode(code).pipe(catchError(() => of(code)))
      );
      forkJoin(requests).subscribe(sigles => {
        this.etablissementsDisplayLabels = sigles;
        this.isResolvingEtab = false;
      });
      return;
    }

    // Cas 2 : IES présent
    this.isResolvingIes = true;
    this.isResolvingEtab = etabCodes.length > 0;

    this.userService.getSigleByCode(iesCode)
      .pipe(catchError(() => of(iesCode)))
      .subscribe(iesSigle => {

        // 2.a) sigle IES → label racine
        this.groupService.getRootGroups()
          .pipe(catchError(() => of([] as string[])))
          .subscribe(rootLabels => {
            const matchedLabel = rootLabels.find(
              l => this.extractSigle(l) === iesSigle
            );
            this.iesDisplayLabel = matchedLabel || iesSigle;
            this.isResolvingIes = false;
          });

        // 2.b) codes étabs → sigles → labels
        if (etabCodes.length === 0) {
          this.isResolvingEtab = false;
          return;
        }

        this.groupService.getSubgroupsByParentSigle(iesSigle)
          .pipe(catchError(() => of([] as string[])))
          .subscribe(etabLabels => {
            const requests = etabCodes.map(code =>
              this.userService.getSigleByCode(code)
                .pipe(catchError(() => of(code)))
            );

            forkJoin(requests).subscribe(sigles => {
              this.etablissementsDisplayLabels = sigles.map(sigle => {
                const match = etabLabels.find(
                  l => this.extractSigle(l) === sigle
                );
                return match || sigle;
              });
              this.isResolvingEtab = false;
            });
          });
      });
  }

  /** Extrait le sigle entre parenthèses d'un label « Nom (SIGLE) » */
  private extractSigle(label: string): string {
    const match = label.match(/\(([^)]+)\)\s*$/);
    return match ? match[1].trim() : label.trim();
  }
}